import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { userApiService, UpdateProfileRequest } from '../services/userApi';
import './UserInfoModal.css';

interface UserInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdateSuccess: () => void;
}

interface Province {
  code: string;
  name: string;
}

// Danh sách tỉnh thành Việt Nam (cập nhật 2025)
const PROVINCES: Province[] = [
  // Thành phố trực thuộc trung ương
  { code: 'HN', name: 'Thành phố Hà Nội' },
  { code: 'HCM', name: 'Thành phố Hồ Chí Minh' },
  { code: 'HP', name: 'Thành phố Hải Phòng' },
  { code: 'DN', name: 'Thành phố Đà Nẵng' },
  { code: 'CT', name: 'Thành phố Cần Thơ' },
  { code: 'TTH', name: 'Thành phố Huế' },
  // Các tỉnh theo thứ tự alphabet
  { code: 'AG', name: 'An Giang' },
  { code: 'BN', name: 'Bắc Ninh' },
  { code: 'CM', name: 'Cà Mau' },
  { code: 'CB', name: 'Cao Bằng' },
  { code: 'DL', name: 'Đắk Lắk' },
  { code: 'DB', name: 'Điện Biên' },
  { code: 'DNai', name: 'Đồng Nai' },
  { code: 'DT', name: 'Đồng Tháp' },
  { code: 'GL', name: 'Gia Lai' },
  { code: 'HT', name: 'Hà Tĩnh' },
  { code: 'HY', name: 'Hưng Yên' },
  { code: 'KH', name: 'Khánh Hòa' },
  { code: 'LC', name: 'Lai Châu' },
  { code: 'LD', name: 'Lâm Đồng' },
  { code: 'LS', name: 'Lạng Sơn' },
  { code: 'LCai', name: 'Lào Cai' },
  { code: 'NA', name: 'Nghệ An' },
  { code: 'NB', name: 'Ninh Bình' },
  { code: 'PT', name: 'Phú Thọ' },
  { code: 'QG', name: 'Quảng Ngãi' },
  { code: 'QN', name: 'Quảng Ninh' },
  { code: 'QT', name: 'Quảng Trị' },
  { code: 'SL', name: 'Sơn La' },
  { code: 'TN', name: 'Tây Ninh' },
  { code: 'TNguyen', name: 'Thái Nguyên' },
  { code: 'TH', name: 'Thanh Hóa' },
  { code: 'TQ', name: 'Tuyên Quang' },
  { code: 'VL', name: 'Vĩnh Long' },
];

const CURRENT_YEAR = new Date().getFullYear();
const BIRTH_YEARS = Array.from({ length: 80 }, (_, i) => CURRENT_YEAR - 18 - i); // From 18 to 98 years old

const UserInfoModal: React.FC<UserInfoModalProps> = ({
  isOpen,
  onClose,
  onUpdateSuccess
}) => {
  const { user } = useAuth();
  const [selectedProvince, setSelectedProvince] = useState('');
  const [ward, setWard] = useState('');
  const [gender, setGender] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState('');

  // Load current user profile data when modal opens
  useEffect(() => {
    const loadUserProfile = async () => {
      if (isOpen) {
        setLoadingProfile(true);
        setError('');
        
        try {
          const response = await userApiService.getProfile();
          
          if (response.success && response.data) {
            const userData = response.data;
            
            // Fill form with existing data
            // Find province code from name
            const provinceMatch = PROVINCES.find(p => p.name === userData.province);
            setSelectedProvince(provinceMatch?.code || '');
            
            setWard(userData.ward || '');
            setGender(userData.gender || '');
            setBirthYear(userData.birthYear?.toString() || '');
          } else {
            console.log('Could not load profile data:', response.error);
            // Don't show error to user for missing profile data
          }
        } catch (error) {
          console.error('Error loading profile:', error);
          // Don't show error to user for profile loading issues
        } finally {
          setLoadingProfile(false);
        }
      }
    };

    loadUserProfile();
  }, [isOpen]);

  const handleSave = async () => {
    // Validate required fields
    if (!selectedProvince || !ward.trim() || !gender || !birthYear) {
      setError('Vui lòng điền đầy đủ thông tin');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Build update request
      const provinceObj = PROVINCES.find(p => p.code === selectedProvince);
      
      const updateData: UpdateProfileRequest = {
        province: provinceObj?.name || '',
        ward: ward.trim(),
        gender: gender as 'male' | 'female' | 'other',
        birthYear: parseInt(birthYear)
      };

      console.log('Updating user info:', updateData);

      // Call API to update user info
      const result = await userApiService.updateProfile(updateData);

      if (result.success) {
        console.log('Profile updated successfully:', result.data);
        onUpdateSuccess();
        handleClose();
      } else {
        setError(result.error || 'Có lỗi xảy ra khi cập nhật thông tin');
      }
    } catch (error: any) {
      console.error('Error updating user info:', error);
      setError(error.message || 'Có lỗi xảy ra khi cập nhật thông tin');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedProvince('');
    setWard('');
    setGender('');
    setBirthYear('');
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="user-info-modal-overlay" onClick={handleClose}>
      <div className="user-info-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="user-info-modal-header">
          <h2>Cập nhật thông tin</h2>
          <button className="close-button" onClick={handleClose}>
            ×
          </button>
        </div>

        <div className="user-info-modal-body">
          {loadingProfile && (
            <div className="loading-indicator">
              <p>Đang tải thông tin...</p>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="province">Tỉnh/Thành phố *</label>
            <select
              id="province"
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              disabled={loading || loadingProfile}
            >
              <option value="">Chọn tỉnh/thành phố</option>
              {PROVINCES.map(province => (
                <option key={province.code} value={province.code}>
                  {province.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="ward">Phường/Xã *</label>
            <input
              id="ward"
              type="text"
              value={ward}
              onChange={(e) => setWard(e.target.value)}
              placeholder="Nhập tên phường/xã"
              disabled={loading || loadingProfile}
            />
          </div>

          <div className="form-group">
            <label htmlFor="gender">Giới tính *</label>
            <select
              id="gender"
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              disabled={loading || loadingProfile}
            >
              <option value="">Chọn giới tính</option>
              <option value="male">Nam</option>
              <option value="female">Nữ</option>
              <option value="other">Khác</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birth-year">Năm sinh *</label>
            <select
              id="birth-year"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              disabled={loading || loadingProfile}
            >
              <option value="">Chọn năm sinh</option>
              {BIRTH_YEARS.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="form-actions">
            <button
              className="secondary-button"
              onClick={handleClose}
              disabled={loading || loadingProfile}
            >
              Hủy
            </button>
            <button
              className="primary-button"
              onClick={handleSave}
              disabled={loading || loadingProfile || !selectedProvince || !ward.trim() || !gender || !birthYear}
            >
              {loading ? 'Đang lưu...' : 'Lưu thông tin'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfoModal;
